/**
 * API Service for communicating with the backend
 */

import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();
const API_BASE_URL = 'https://daring-positively-owl.ngrok-free.app'; // TODO: change this

/**
 * Get authentication headers including Monday.com session token
 *
 * @returns {Promise<HeadersInit>} Headers with authentication token
 * @throws {Error} If session token cannot be retrieved
 */
const getAuthHeaders = async (): Promise<HeadersInit> => {
  try {
    const sessionTokenResponse = await monday.get('sessionToken');

    // Extract the actual token from the response object
    const token = sessionTokenResponse?.data;

    if (!token || typeof token !== 'string') {
      console.error('Invalid token format:', sessionTokenResponse);
      throw new Error('Session token is empty, undefined, or invalid format');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'ngrok-skip-browser-warning': 'true',
    };
  } catch (error) {
    console.error('Failed to get session token:', error);
    throw new Error('Authentication failed: Unable to retrieve session token');
  }
};

/**
 * Handle API authentication errors
 *
 * @param {Response} response - Fetch response object
 * @returns {void}
 * @throws {Error} If response indicates authentication failure
 */
const handleAuthError = (response: Response): void => {
  if (response.status === 401) {
    throw new Error('Unauthorized: Invalid or expired session token');
  }
  if (response.status === 403) {
    throw new Error('Forbidden: Insufficient permissions');
  }
};

/**
 * Configuration creation payload
 */
export interface ConfigurationPayload {
  board_id: string;
  source_type: 'livechat' | 'sms-twilio' | 'whatsapp-twilio';
  source_main: string;
  name?: string;
  tokens?: {
    provider?: string;
    account_sid?: string;
    auth_token?: string;
    api_key?: string;
    base_url?: string;
  };
}

/**
 * Widget creation payload
 */
export interface WidgetPayload {
  name: string;
}

/**
 * Widget data from backend
 */
export interface WidgetData {
  id: number;
  name: string;
  enabled: boolean;
  createdAt: string;
  organization_id: string;
}

/**
 * Configuration data from backend
 */
export interface ConfigurationData {
  id: string;
  name?: string;
  source_type: 'livechat' | 'sms-twilio' | 'whatsapp-twilio';
  source_main: string;
  sink_type: string;
  active: boolean;
  created_at: string;
  widget?: {
    id: number;
    name: string;
    enabled: boolean;
  } | null;
  board_id?: string;
  phone_number?: string;
  integration_type?: string;
  tokens?: {
    provider?: string;
    account_sid?: string;
    auth_token?: string;
    api_key?: string;
    base_url?: string;
  };
}

/**
 * API response interface
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Create a configuration on the backend
 *
 * @param {ConfigurationPayload} payload - Configuration data
 * @returns {Promise<ApiResponse>} Response from the API
 */
export const createConfiguration = async (
  payload: ConfigurationPayload
): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/monday/configurations`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    handleAuthError(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create configuration');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Update a configuration on the backend
 *
 * @param {string} configId - Configuration ID
 * @param {Partial<ConfigurationPayload>} payload - Configuration data to update
 * @returns {Promise<ApiResponse>} Response from the API
 */
export const updateConfiguration = async (
  configId: string,
  payload: Partial<ConfigurationPayload>
): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/monday/configurations/${configId}`, {
      method: 'PATCH',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    handleAuthError(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to update configuration');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Delete a configuration on the backend
 *
 * @param {string} configId - Configuration ID
 * @returns {Promise<ApiResponse>} Response from the API
 */
export const deleteConfiguration = async (configId: string): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/monday/configurations/${configId}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });

    handleAuthError(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete configuration');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Create a chat widget on the backend
 *
 * @param {WidgetPayload} payload - Widget data
 * @returns {Promise<ApiResponse<WidgetData>>} Response from the API
 */
export const createWidget = async (
  payload: WidgetPayload
): Promise<ApiResponse<WidgetData>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/monday/chat_widgets`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    handleAuthError(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create widget');
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Delete a chat widget on the backend
 *
 * @param {number} widgetId - Widget ID
 * @returns {Promise<ApiResponse>} Response from the API
 */
export const deleteWidget = async (
  widgetId: number
): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/monday/chat_widgets/${widgetId}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });

    handleAuthError(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete widget');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Fetch all configurations from the backend
 *
 * @returns {Promise<ApiResponse<ConfigurationData[]>>} Response from the API
 */
export const fetchConfigurations = async (): Promise<ApiResponse<ConfigurationData[]>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/monday/configurations`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });

    handleAuthError(response);

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch configurations');
    }

    return {
      success: true,
      data: data.data || data,
    };
  } catch (error) {
    console.error('API Error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

